import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {Observable} from 'rxjs';

import {InvoiceModalComponent} from './invoice-modal/invoice-modal.component';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {InvoicesService} from '../invoices.service';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {InvoiceDetailsConverterService} from './view-models/invoice-details-converter.service';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {InvoiceDetailsViewModel} from './view-models/invoice-detail-view.model';
import {ModalService} from '@shared/services/modal.service';
import {InvoiceDetailsListDTO} from '@dto/invoices/invoice-details-list-dto.model';
import {Keys} from '@shared/keys';
import {InvoiceModalEventData} from './invoice-modal/invoice-modal-event-data';
import {InputValidationUtil} from '@shared/utils/input-validation.util';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {InvoiceDetailsCardComponent} from './invoice-details-card/invoice-details-card.component';

@Component({
    selector: 'app-view-invoice-details',
    templateUrl: './view-invoice-details.component.html',
    styleUrls: ['./view-invoice-details.component.scss'],
    providers: [ClientsideDataService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TableTemplateComponent,
        LoadingAnimationComponent,
        InvoiceDetailsCardComponent
    ]
})
export class ViewInvoiceDetailsComponent implements OnInit {

  readonly clientCode = input<string>();
  readonly currencyCode = input<string | undefined>(undefined);
  readonly padding = input(true);

  private readonly route = inject(ActivatedRoute);
  private readonly invoicesService = inject(InvoicesService);
  private readonly modalService = inject(ModalService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly clientSideDataService = inject(ClientsideDataService<InvoiceDetailsViewModel>);
  private readonly invoiceDetailsConverterService = inject(InvoiceDetailsConverterService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly SEARCH_FIELDS: Keys<InvoiceDetailsViewModel> = ['InvoiceNumber'];

  dataLoading = signal(true);
  dataAvailable = signal(false);
  dataEmpty = signal(false);
  customerName = signal('');
  columns = signal<TableTemplateColumnConfig[]>([]);
  sortField = signal('InvoiceDate');
  sortOrder = signal(-1);
  searchString = signal('');
  pageNumber = signal(1);
  pageSize = signal(20);
  currentPage = signal<PageViewModel<InvoiceDetailsViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });

  invoiceDetailsViewModels: InvoiceDetailsViewModel[] = [];

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    const rawClientCode = this.clientCode() ?? this.route.snapshot.params['id'];
    const rawCurrencyCode = this.currencyCode() ?? this.route.snapshot.params['currencyCode'];

    const safeClientCode = this.tryValidateClientCode(rawClientCode);
    if (!safeClientCode) {
      return;
    }

    const safeCurrencyCode = this.tryValidateCurrencyCode(rawCurrencyCode);

    this.populateColumns();
    const storedSort = this.sessionStorageService.getSort(SortedTable.InvoiceDetails);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    let invoiceDetailsList$: Observable<InvoiceDetailsListDTO>;

    if (safeCurrencyCode) {
      invoiceDetailsList$ = this.invoicesService.getInvoiceDetailsByCurrency(safeClientCode, safeCurrencyCode);
    } else {
      invoiceDetailsList$ = this.invoicesService.getInvoiceDetails(safeClientCode);
    }

    invoiceDetailsList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(invoiceDetailsListDTO => {
      this.customerName.set(invoiceDetailsListDTO.CustomerName);
      this.invoiceDetailsViewModels = this.invoiceDetailsConverterService.convert(invoiceDetailsListDTO.InvoiceDetailsList);
      this.clientSideDataService.initialize(this.invoiceDetailsViewModels);
      this.updateData();
      this.dataLoading.set(false);
      this.dataEmpty.set(this.invoiceDetailsViewModels.length === 0);
      this.dataAvailable.set(!this.dataEmpty());
    });
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber.set(1);
    this.updateData();
  }

  search(searchTerm: string): void {
    this.searchString.set(searchTerm);
    this.pageNumber.set(1);
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber.set(targetPage);
    this.updateData();
  }

  rowClicked(dataViewModel: DataViewModel): void {
    const invoiceDetailsVM = dataViewModel as InvoiceDetailsViewModel;
    const invoiceIdData: InvoiceModalEventData = {
      InvoiceNumber: invoiceDetailsVM.InvoiceNumber,
      BillingNumber: invoiceDetailsVM.BillingNumber
    };
    this.modalService.openModal(InvoiceModalComponent, invoiceIdData);
  }

  populateColumns(): void {
    this.columns.set([
      {
        title: 'Invoice No.',
        dataProperty: 'InvoiceNumber',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '21%',
        dataFocusable: true,
        headingFocusable: true
      },
      {
        title: 'Invoice Date',
        dataProperty: 'InvoiceDate',
        cellType: CellType.Date,
        sortable: true,
        alignment: Alignment.Left,
        width: '21%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Currency',
        dataProperty: 'CurrencyCode',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '13%', // same as status
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Amount',
        dataProperty: 'Amount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '16%', // same as remaining amount
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Status',
        dataProperty: 'StatusIcon',
        cellType: CellType.Icon,
        sortable: true,
        alignment: Alignment.Center,
        width: '13%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Amount Remaining',
        dataProperty: 'AmountRemaining',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '16%',
        dataFocusable: false,
        headingFocusable: true
      }
    ]);
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(SortedTable.InvoiceDetails, this.sortField(), this.sortOrder());
  }

  private updateData(): void {
    this.currentPage.set(this.clientSideDataService.generatePage(
      this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString(),
      this.pageNumber(),
      this.pageSize()
    ));
  }

  private tryValidateClientCode(clientCode: string | undefined): string | null {
    if (!clientCode) {
      this.handleParameterValidationError('clientCode', new Error('Client code is required'));
      return null;
    }

    try {
      return InputValidationUtil.validateClientCode(clientCode);
    } catch (error) {
      this.handleParameterValidationError('clientCode', error);
      return null;
    }
  }

  private tryValidateCurrencyCode(currencyCode: string | undefined): string | undefined {
    if (!currencyCode) {
      return undefined;
    }

    try {
      return InputValidationUtil.validateCurrencyCode(currencyCode);
    } catch (error) {
      this.handleParameterValidationError('currencyCode', error);
      return undefined;
    }
  }

  private handleParameterValidationError(parameterName: string, error: unknown): void {
    console.error(`Invalid ${parameterName} provided to ViewInvoiceDetailsComponent`, error);
    this.invoiceDetailsViewModels = [];
    this.clientSideDataService.initialize(this.invoiceDetailsViewModels);
    this.updateData();
    this.dataLoading.set(false);
    this.dataEmpty.set(true);
    this.dataAvailable.set(false);
  }
}
